'use client';

import {
    createContext,
    MouseEventHandler,
    PointerEventHandler,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";
import { ANIMATION_DURATION_MS, SNAP_THRESHOLD } from "../types/constants";
import useIsDesktop from "@/hooks/useDesktop";

interface SplitContentContextType {
    orientation: Orientation,
    splitPosition: number,
    setSplitPosition: (position: number) => void,
    isLeading: boolean,
    isHorizontal: boolean,
    maxSplitPosition: number,
    dragContainerRef: React.RefObject<HTMLDivElement | null>,
    isSnapAnimate: boolean,
    resizingDisabled: boolean,
    handlePointerDown: PointerEventHandler<HTMLDivElement>,
    handleDragHandleClick: MouseEventHandler<HTMLDivElement>,
    backgroundMode: BackgroundMode,
}

const SplitContentContext = createContext<SplitContentContextType | undefined>(undefined);

export function useSplitContent() {
    const context = useContext(SplitContentContext);
    if (!context) {
        throw new Error("'useSplitContent' must be used within 'SplitContentProvider'");
    }
    return context;
}

interface SplitContentProviderProps {
    children: ReactNode;
    initialSplitPosition?: number;
    maxSplitPosition?: number;
    resizingDisabled?: boolean;
    orientation?: Orientation;
    backgroundMode?: BackgroundMode;
}

export default function SplitContentProvider({
    children,
    initialSplitPosition = 50,
    maxSplitPosition = initialSplitPosition,
    resizingDisabled = false,
    orientation = 'row',
    backgroundMode = 'fill',
}: SplitContentProviderProps) {

    const isDesktop = useIsDesktop();

    if (!isDesktop && (orientation === 'row' || orientation === 'row-reverse')) {
        orientation = 'column-reverse';
    }

    if (!isDesktop) {
        maxSplitPosition = 70;
        initialSplitPosition = 30;
        backgroundMode = 'adaptive';
    }

    const isHorizontal = orientation === 'row' || orientation === 'row-reverse';
    const isLeading = orientation === 'row' || orientation === 'column';
    const normalizedMaxSplitPosition = Math.min(Math.max(maxSplitPosition, 0), 100);
    const splitPositionMin = isLeading ? 0 : 100 - normalizedMaxSplitPosition;
    const splitPositionMax = isLeading ? normalizedMaxSplitPosition : 100;

    const clampSplitPosition = useCallback((position: number) => {
        return Math.min(Math.max(position, splitPositionMin), splitPositionMax);
    }, [splitPositionMax, splitPositionMin]);

    const dragContainerRef = useRef<HTMLDivElement>(null);
    const isDraggingRef = useRef(false);
    const initialClampedSplitPosition = clampSplitPosition(initialSplitPosition);
    const splitPositionRef = useRef<number>(initialClampedSplitPosition);
    const rafRef = useRef<number | null>(null);
    const pendingSplitPositionRef = useRef<number | null>(null);

    const [isSnapAnimate, setIsSnapAnimate] = useState<boolean>(false);
    const snapTimeoutRef = useRef<number | null>(null);

    const [splitPosition, setSplitPositionState] = useState<number>(initialClampedSplitPosition);

    const setSplitPosition = useCallback((position: number) => {
        const next = clampSplitPosition(position);
        splitPositionRef.current = next;
        setSplitPositionState((previous) => (previous === next ? previous : next));
    }, [clampSplitPosition]);

    useEffect(() => {
        setSplitPositionState((previous) => {
            const next = clampSplitPosition(previous);
            splitPositionRef.current = next;
            return previous === next ? previous : next;
        });
    }, [clampSplitPosition]);

    useEffect(() => {
        if (snapTimeoutRef.current) {
            window.clearTimeout(snapTimeoutRef.current);
            snapTimeoutRef.current = null;
        }
        if (!isSnapAnimate) return undefined;

        snapTimeoutRef.current = window.setTimeout(() => {
            setIsSnapAnimate(false);
            snapTimeoutRef.current = null;
        }, ANIMATION_DURATION_MS);

        return () => {
            if (snapTimeoutRef.current) {
                window.clearTimeout(snapTimeoutRef.current);
                snapTimeoutRef.current = null;
            }
        };
    }, [isSnapAnimate]);

    const handlePointerDown: PointerEventHandler<HTMLDivElement> = useCallback((event) => {
        if (resizingDisabled) return;
        if (event.button !== 0) return;

        event.currentTarget.setPointerCapture(event.pointerId);
        isDraggingRef.current = true;
        document.body.classList.add('no-select');
    }, [resizingDisabled]);

    const stopDragging = useCallback(() => {
        if (!isDraggingRef.current) return;
        isDraggingRef.current = false;
        document.body.classList.remove('no-select');
    }, []);

    const applySplitPosition = useCallback((position: number) => {
        const next = clampSplitPosition(position);
        if (next === splitPositionRef.current) return;
        pendingSplitPositionRef.current = next;

        if (rafRef.current !== null) return;
        rafRef.current = window.requestAnimationFrame(() => {
            rafRef.current = null;
            const queuedSplitPosition = pendingSplitPositionRef.current;
            pendingSplitPositionRef.current = null;
            if (queuedSplitPosition === null) return;
            setSplitPosition(queuedSplitPosition);
        });
    }, [clampSplitPosition, setSplitPosition]);

    const handlePointerMove = useCallback((event: PointerEvent) => {
        if (!isDraggingRef.current || resizingDisabled) return;

        const rect = dragContainerRef.current?.getBoundingClientRect();
        if (!rect) return;

        if (isHorizontal) {
            const w = rect.width || 0;
            if (!w) return;
            const relativeX = ((event.clientX - rect.left) / w) * 100;
            applySplitPosition(relativeX);
            return;
        }

        const h = rect.height || 0;
        if (!h) return;
        const relativeY = ((event.clientY - rect.top) / h) * 100;
        applySplitPosition(relativeY);
    }, [applySplitPosition, isHorizontal, resizingDisabled]);

    const handleDragHandleClick: MouseEventHandler<HTMLDivElement> = useCallback(() => {
        if (resizingDisabled) return;
        setIsSnapAnimate(true);
        if (Math.abs(splitPosition - splitPositionMax) < 0.1) {
            setSplitPosition(splitPositionMin);
        } else if (Math.abs(splitPosition - splitPositionMin) < 0.1) {
            setSplitPosition(splitPositionMax);
        } else if (splitPosition < splitPositionMin + SNAP_THRESHOLD) {
            setSplitPosition(splitPositionMin);
        } else if (splitPosition > splitPositionMax - SNAP_THRESHOLD) {
            setSplitPosition(splitPositionMax);
        }
    }, [resizingDisabled, setSplitPosition, splitPosition, splitPositionMax, splitPositionMin]);

    useEffect(() => {
        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', stopDragging);
        window.addEventListener('pointercancel', stopDragging);

        return () => {
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', stopDragging);
            window.removeEventListener('pointercancel', stopDragging);
        };
    }, [handlePointerMove, stopDragging]);

    useEffect(() => {
        return () => {
            if (rafRef.current !== null) {
                window.cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
            document.body.classList.remove('no-select');
        };
    }, []);

    const contextValue = useMemo(() => ({
        orientation,
        isLeading,
        isHorizontal,
        splitPosition,
        setSplitPosition,
        maxSplitPosition: normalizedMaxSplitPosition,
        dragContainerRef,
        isSnapAnimate,
        resizingDisabled,
        handlePointerDown,
        handleDragHandleClick,
        backgroundMode,
    }), [
        orientation,
        isLeading,
        isHorizontal,
        splitPosition,
        setSplitPosition,
        normalizedMaxSplitPosition,
        isSnapAnimate,
        resizingDisabled,
        handlePointerDown,
        handleDragHandleClick,
        backgroundMode,
    ]);

    return (
        <SplitContentContext.Provider value={contextValue}>
            {children}
        </SplitContentContext.Provider>
    );
}
